import type { File } from '~/types';
import { computed, onMounted, onUnmounted, ref } from 'vue';

/**
 * Reusable gallery modal state machine, extracted from VGallery.
 *
 * Handles open/close, index navigation (with wrap-around), and progressive
 * image preloading. `openGallery(items, startIndex)` opens the modal scoped to
 * a specific gallery array and RESETS all state — this is what allows a
 * project gallery to open a different project's full gallery cleanly.
 */
export function useGalleryModal() {
  const isOpen = ref(false);
  const currentItemIdx = ref(0);
  const isHelpOpen = ref(false);
  const modalImageIndexes = ref<number[]>([]);
  const modalImageTimers: ReturnType<typeof setTimeout>[] = [];
  const items = ref<File[]>([]);

  const currentItem = computed(() => items.value[currentItemIdx.value]);

  function addModalImageIndex(index: number) {
    if (index < 0 || index >= items.value.length || modalImageIndexes.value.includes(index)) return;
    modalImageIndexes.value = [...modalImageIndexes.value, index];
  }

  function clearTimers() {
    while (modalImageTimers.length) {
      const timer = modalImageTimers.pop();
      if (timer) clearTimeout(timer);
    }
  }

  function queueModalImages(startIndex: number) {
    clearTimers();
    modalImageIndexes.value = [startIndex];

    const total = items.value.length;
    if (total <= 1) return;

    const nextIndex = startIndex === total - 1 ? 0 : startIndex + 1;
    const prevIndex = startIndex === 0 ? total - 1 : startIndex - 1;
    const priorityIndexes = Array.from(new Set([nextIndex, prevIndex])).filter((index) => index !== startIndex);

    modalImageTimers.push(
      setTimeout(() => {
        for (const index of priorityIndexes) addModalImageIndex(index);
      }, 150),
    );

    const remainingIndexes = items.value
      .map((_, index) => index)
      .filter((index) => index !== startIndex && !priorityIndexes.includes(index));

    remainingIndexes.forEach((index, loadIdx) => {
      modalImageTimers.push(
        setTimeout(
          () => {
            addModalImageIndex(index);
          },
          450 + loadIdx * 150,
        ),
      );
    });
  }

  /**
   * Open the modal scoped to a specific gallery array. Resets index, queued
   * images, and timers before opening — critical for switching projects.
   */
  function openGallery(gallery: File[], startIndex = 0) {
    items.value = gallery;
    currentItemIdx.value = startIndex;
    isOpen.value = true;
    queueModalImages(startIndex);
  }

  /** Open at a specific index within the current items (used by grouped mode). */
  function openGalleryAt(index: number) {
    currentItemIdx.value = index;
    isOpen.value = true;
    queueModalImages(index);
  }

  function next() {
    currentItemIdx.value = currentItemIdx.value === items.value.length - 1 ? 0 : currentItemIdx.value + 1;
    queueModalImages(currentItemIdx.value);
  }

  function prev() {
    currentItemIdx.value = currentItemIdx.value === 0 ? items.value.length - 1 : currentItemIdx.value - 1;
    queueModalImages(currentItemIdx.value);
  }

  function close() {
    isOpen.value = false;
    clearTimers();
    modalImageIndexes.value = [];
  }

  function toggleHelp() {
    isHelpOpen.value = !isHelpOpen.value;
  }

  function onKeydown(e: KeyboardEvent) {
    if (!isOpen.value) return;

    if (e.key === 'Escape') {
      close();
    } else if (e.key === 'ArrowRight') {
      next();
    } else if (e.key === 'ArrowLeft') {
      prev();
    }
  }

  onMounted(() => {
    window.addEventListener('keydown', onKeydown);
  });

  onUnmounted(() => {
    window.removeEventListener('keydown', onKeydown);
    clearTimers();
  });

  return {
    isOpen,
    currentItemIdx,
    isHelpOpen,
    modalImageIndexes,
    currentItem,
    items,
    openGallery,
    openGalleryAt,
    next,
    prev,
    close,
    toggleHelp,
    queueModalImages,
    clearTimers,
  };
}
